import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import regression from "regression";

// Generate polynomial regression curve with specified degree
const generatePolynomialCurve = (data, degree) => {
  const result = regression.polynomial(data, { order: degree });

  // Generate smoother curve points
  const regressionPoints = [];
  const xMin = Math.min(...data.map((d) => d[0]));
  const xMax = Math.max(...data.map((d) => d[0]));

  for (let i = xMin; i <= xMax; i += 0.1) {
    const y = result.predict(i)[1];
    regressionPoints.push({
      x: parseFloat(i.toFixed(2)),
      y: parseFloat(y.toFixed(2)),
    });
  }

  return regressionPoints;
};

const PolynomialRegressionChart = ({ chartUtils }) => {
  const datasets = chartUtils?.series;

  const [chartData, setChartData] = useState([]);
  const [options, setOptions] = useState({});

  useEffect(() => {
    const series = [];
    const markerSizes = [];

    datasets.forEach((dataset, index) => {
      const regressionData = dataset.data.map((point) => [point.x, point.y]);

      // Add polynomial regression line (3rd order)
      series.push({
        name: `${dataset.name} `,
        type: "line",
        data: generatePolynomialCurve(
          regressionData,
          chartUtils?.type?.degree || 3
        ),
        color: dataset.color,
        stroke: { width: 2, curve: "smooth", dashArray: [0] },
        markers: { size: 0 },
      });
      markerSizes.push(0);

      // Add scatter points for this dataset
      series.push({
        name: dataset.name,
        type: "scatter",
        data: dataset.data,
        color: dataset.color,
        markers: {
          size: 7,
          strokeWidth: 2,
          strokeColors: dataset.color,
          fillColors: dataset.color,
          shape: "circle",
          hover: { size: 9 },
        },
      });
      markerSizes.push(7);
    });

    setChartData(series);

    // Configure chart options
    setOptions({
      chart: {
        type: "line",
        zoom: {
          enabled: true,
          type: "xy",
        },
        toolbar: {
          show: true,
        },
        background: "#FFFFFF",
        animations: {
          enabled: true,
        },
      },
      grid: {
        show: true,
        borderColor: "#e0e0e0",
        strokeDashArray: 0,
        position: "back",
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      // title: {
      //   text: "Multiple Datasets with Polynomial Regression",
      //   align: "center",
      //   style: {
      //     fontSize: "16px",
      //     fontWeight: 600,
      //   },
      // },
      xaxis: {
        title: {
          text: chartUtils?.axis?.x || "Date",
          style: {
            fontSize: "14px",
            fontWeight: 500,
          },
        },
        min: -1,
        max: 8,
        tickAmount: 9,
        labels: {
          formatter: function (val) {
            return val.toFixed(0);
          },
        },
      },
      yaxis: {
        title: {
          text: chartUtils?.axis?.y || "Y",
          style: {
            fontSize: "14px",
            fontWeight: 500,
          },
        },
        min: 0,
        max: 200,
        tickAmount: 10,
        labels: {
          formatter: function (val) {
            return val.toFixed(0);
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        shared: false,
        intersect: true,
        x: {
          show: true,
        },
        y: {
          formatter: function (val) {
            return val.toFixed(2);
          },
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        offsetY: 5,
        markers: {
          width: 12,
          height: 12,
          radius: 6,
        },
      },
      markers: {
        size: markerSizes,
        hover: {
          sizeOffset: 2,
        },
      },
    });
  }, []);

  return (
    <>
      {chartUtils?.chart_type === "POLYNOMIALCHART" && chartData.length > 0 && (
        <Chart
          options={options}
          series={chartData}
          type="line"
          height={450}
          width="100%"
        />
      )}
    </>
  );
};

export default PolynomialRegressionChart;
